import React from 'react';
import {cn} from "../utils/style";
import {useTranslation} from "react-i18next";
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    ArrowPathRoundedSquareIcon,
    NoSymbolIcon,
    ClockIcon,
    ShieldExclamationIcon
} from "@heroicons/react/24/outline";

function InvoiceBanner({status, backendMessage, className, antiFraud}) {
    const {t} = useTranslation();
    if (status === 'pending') return null;

    const Messages = {
        not_found: t('invoice_banner.not_found'),
        unknown_error: t('invoice_banner.unknown_error'),
        completed: t('invoice_banner.completed'),
        paid: t('invoice_banner.completed'),
        failed: t('invoice_banner.failed'),
        refunded: t('invoice_banner.refunded'),
        canceled: t('invoice_banner.canceled'),
        expired: t('invoice_banner.expired'),
        on_hold: t('invoice_banner.on_hold'),
        blocked: t('invoice_banner.blocked'),
    }

    let message = backendMessage || Messages[status] || '';

    const getStatusConfig = () => {
        switch (status) {
            case 'completed':
            case 'paid':
                return {
                    icon: <CheckCircleIcon className="h-6 w-6" />,
                    bgClass: 'bg-green-50/80',
                    borderClass: 'border-green-100/50',
                    textClass: 'text-green-800',
                    iconClass: 'text-green-500',
                    accentClass: 'bg-green-500/10'
                };
            case 'failed':
            case 'expired':
            case 'not_found':
            case 'unknown_error':
                return {
                    icon: <XCircleIcon className="h-6 w-6" />,
                    bgClass: 'bg-rose-50/80',
                    borderClass: 'border-rose-100/50',
                    textClass: 'text-rose-800',
                    iconClass: 'text-rose-500',
                    accentClass: 'bg-rose-500/10'
                };
            case 'refunded':
                return {
                    icon: <ArrowPathRoundedSquareIcon className="h-6 w-6" />,
                    bgClass: 'bg-teal-50/80',
                    borderClass: 'border-teal-100/50',
                    textClass: 'text-teal-800',
                    iconClass: 'text-teal-500',
                    accentClass: 'bg-teal-500/10'
                };
            case 'canceled':
                return {
                    icon: <NoSymbolIcon className="h-6 w-6" />,
                    bgClass: 'bg-gray-50/80',
                    borderClass: 'border-gray-100/50',
                    textClass: 'text-gray-800',
                    iconClass: 'text-gray-500',
                    accentClass: 'bg-gray-500/10'
                };
            case 'on_hold':
                return {
                    icon: <ClockIcon className="h-6 w-6" />,
                    bgClass: 'bg-amber-50/80',
                    borderClass: 'border-amber-100/50',
                    textClass: 'text-amber-800',
                    iconClass: 'text-amber-500',
                    accentClass: 'bg-amber-500/10'
                };
            case 'blocked':
                return {
                    icon: <ShieldExclamationIcon className="h-6 w-6" />,
                    bgClass: 'bg-red-50/80',
                    borderClass: 'border-red-100/50',
                    textClass: 'text-red-800',
                    iconClass: 'text-red-500',
                    accentClass: 'bg-red-500/10'
                };
            default:
                return {
                    icon: <ExclamationCircleIcon className="h-6 w-6" />,
                    bgClass: 'bg-cyan-50/80',
                    borderClass: 'border-cyan-100/50',
                    textClass: 'text-cyan-800',
                    iconClass: 'text-cyan-500',
                    accentClass: 'bg-cyan-500/10'
                };
        }
    };

    if (antiFraud) {
        return (
            <div className={cn(
                "p-6 mt-3 rounded-[2rem] border shadow-sm animate-fade-in transition-all duration-300 backdrop-blur-md",
                "bg-rose-50/90 border-rose-100/50 text-rose-900",
                "flex items-start gap-4",
                className
            )}>
                <div className="bg-rose-500/10 p-2.5 rounded-2xl flex-shrink-0 text-rose-600">
                    <ShieldExclamationIcon className="h-6 w-6" />
                </div>
                <div className="flex-1 space-y-1">
                    <p className="font-black text-base tracking-tight">{t('invoice_banner.anti_fraud_title')}</p>
                    <p className="text-sm font-medium text-rose-700/80 leading-relaxed">{t('invoice_banner.anti_fraud_description')}</p>
                </div>
            </div>
        );
    }

    const config = getStatusConfig();

    return (
        <div className={cn(
            "p-6 mt-3 rounded-[2rem] border shadow-sm animate-fade-in transition-all duration-300 backdrop-blur-md",
            config.bgClass,
            config.borderClass,
            config.textClass,
            "flex items-center gap-4",
            className
        )}>
            <div className={cn("p-2.5 rounded-2xl flex-shrink-0", config.accentClass, config.iconClass)}>
                {config.icon}
            </div>
            <div className="flex-1 font-bold text-base tracking-tight">
                {message}
            </div>
        </div>
    );
}

export default InvoiceBanner;